import { useAuth } from '@/context/AuthContext'
import {
  type ConnectionStatus,
  adminSocket,
  connectAdminSocket,
  disconnectAdminSocket
} from '@/lib/ws'
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

interface ConnectionContextValue {
  status: ConnectionStatus
}

const ConnectionContext = createContext<ConnectionContextValue | null>(null)

export function ConnectionProvider({ children }: { children: ReactNode }) {
  const { status: authStatus } = useAuth()
  const [status, setStatus] = useState<ConnectionStatus>(
    adminSocket.getStatus()
  )

  useEffect(() => {
    const unsubscribe = adminSocket.onStatusChange(setStatus)
    return unsubscribe
  }, [])

  useEffect(() => {
    if (authStatus === 'authenticated') {
      connectAdminSocket()
      return () => disconnectAdminSocket()
    }
  }, [authStatus])

  const value = useMemo(() => ({ status }), [status])

  return (
    <ConnectionContext.Provider value={value}>
      {children}
    </ConnectionContext.Provider>
  )
}

export function useConnection(): ConnectionContextValue {
  const ctx = useContext(ConnectionContext)
  if (!ctx)
    throw new Error('useConnection must be used inside <ConnectionProvider>')
  return ctx
}
